import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError, unwrap } from '@/api/envelope';
import {
  listPortalUsers,
  removePortalUser,
  resendPortalInvitation,
  revokePortalInvitation,
} from '@/api/generated';
import { useAuth } from '@/auth/useAuth';
import { useReadOnly } from '@/auth/useViewingAs';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { PageHeader } from '@/components/ui/PageHeader';
import { RowActionsMenu, type RowAction } from '@/components/ui/RowActionsMenu';
import { useToast } from '@/components/ui/useToast';
import { STATUS_LABELS, formatDate, isWritable } from '@/lib/organization';
import {
  ROLE_LABELS,
  STATE_LABELS,
  toPortalUserRow,
  invitationIdOf,
  type PortalUserRow,
} from '@/lib/portalUsers';

import { ChangeRoleDialog } from './ChangeRoleDialog';
import { InviteUserDialog } from './InviteUserDialog';
import { usersKey } from './keys';
import { actionsFor, type UserAction } from './userActions';

/** What a Super Admin has asked for and not yet confirmed. */
type Pending = { kind: 'remove' | 'revoke'; person: PortalUserRow } | null;

/** Live people first, then history, each in the order the backend sent them. */
function inDisplayOrder(rows: PortalUserRow[]): PortalUserRow[] {
  return [...rows.filter((row) => !row.isPast), ...rows.filter((row) => row.isPast)];
}

/** The expiry under an Invited or Expired state, or nothing. */
function expiryText(row: PortalUserRow): string | null {
  if (!row.expiresAt) return null;
  if (row.state === 'invited') return `Expires ${formatDate(row.expiresAt)}`;
  if (row.state === 'expired') return `Expired ${formatDate(row.expiresAt)}`;
  return null;
}

/**
 * The organization's portal users, as its Super Admin manages them.
 *
 * Members, invitations and history in one table, because they are one record
 * on the backend at different points in its life - and because a Super Admin
 * wondering why an address cannot be invited again needs to see that it was
 * revoked, not wonder whether it was ever sent.
 *
 * Two actions show their outcome at once, because it is one row in a known
 * state and a refusal is rare: changing a role (in its dialog) and revoking an
 * invitation, both rolled back with a message if the server refuses. Removing
 * a person waits for the server, because the rule that matters most here -
 * the organization keeps a Super Admin - depends on the whole list, and the
 * list on screen may be out of date. Every refusal shows the server's reason
 * and fetches the list again, because the refusal means what was on screen
 * was wrong.
 */
export function UsersPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();

  // Read at sign-in, so it may be stale. A write refused because of it is
  // shown and the list refetched, like any other refusal.
  const organization = user?.organizations.find((o) => o.id === user.organizationId);
  /*
   * The status comes from the signed-in person's own membership, which staff
   * viewing the organization do not have - so for them the page cannot say
   * whether the organization is suspended, and shows no notice; the banner
   * says why nothing can be changed.
   */
  const viewing = useReadOnly();
  const statusForbids = !isWritable(organization?.status);
  const writable = !statusForbids && !viewing;

  // A fresh dialog for every invitation, so nothing typed last time is shown.
  const [inviting, setInviting] = useState(0);
  const [inviteOpen, setInviteOpen] = useState(false);
  // The person stays when the dialog closes, so it fades out with their name
  // still in it rather than going blank.
  const [roleFor, setRoleFor] = useState<PortalUserRow | null>(null);
  const [roleOpen, setRoleOpen] = useState(false);
  const [pending, setPending] = useState<Pending>(null);

  const query = useQuery({
    queryKey: usersKey,
    queryFn: async () => {
      const reply = await listPortalUsers({ throwOnError: true });
      return (unwrap(reply.data)?.items ?? []).map(toPortalUserRow);
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: usersKey });

  /** Every refusal is told the same way: the server's words, then the truth. */
  const refused = (fallback: string) => async (error: unknown) => {
    setPending(null);
    toast.show({
      message: (error instanceof ApiError ? error.serverMessage : null) ?? fallback,
      severity: 'error',
    });
    await refresh();
  };

  const remove = useMutation({
    mutationFn: (person: PortalUserRow) =>
      removePortalUser({ path: { user_id: person.userId }, throwOnError: true }),
    onSuccess: async (_data, person) => {
      setPending(null);
      toast.show({ message: `${person.name} was removed from the organization.` });
      await refresh();
    },
    onError: refused('That person could not be removed. Please try again.'),
  });

  const resend = useMutation({
    mutationFn: async (person: PortalUserRow) => {
      const reply = await resendPortalInvitation({
        path: { invitation_id: invitationIdOf(person) },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onSuccess: async (data, person) => {
      toast.show(
        data?.email_sent === false
          ? {
              message: `A fresh link was made for ${person.name}, but the email could not be sent. Try Resend again.`,
              severity: 'error',
            }
          : {
              message: `The invitation was sent again to ${person.name}. The previous link no longer works.`,
            },
      );
      await refresh();
    },
    onError: refused('That invitation could not be resent. Please try again.'),
  });

  const revoke = useMutation({
    mutationFn: (person: PortalUserRow) =>
      revokePortalInvitation({
        path: { invitation_id: invitationIdOf(person) },
        throwOnError: true,
      }),
    /*
     * Optimistic: the row leaves the live list the moment Revoke is pressed,
     * shown as the history row the server will keep - Revoked, greyed, at the
     * bottom - which is exactly what the refetch brings back. The list as it
     * was is kept so a refusal can put the row back while the truth arrives.
     */
    onMutate: async (person) => {
      await queryClient.cancelQueries({ queryKey: usersKey });
      const before = queryClient.getQueryData<PortalUserRow[]>(usersKey);
      queryClient.setQueryData<PortalUserRow[]>(usersKey, (rows) =>
        rows?.map((row) =>
          row.membershipId === person.membershipId
            ? { ...row, state: 'revoked' as const, isPast: true }
            : row,
        ),
      );
      setPending(null);
      return { before };
    },
    onSuccess: (_data, person) => {
      toast.show({ message: `The invitation to ${person.name} was revoked.` });
    },
    onError: (error, _person, context) => {
      if (context?.before) queryClient.setQueryData(usersKey, context.before);
      toast.show({
        message:
          (error instanceof ApiError ? error.serverMessage : null) ??
          'That invitation could not be revoked. Please try again.',
        severity: 'error',
      });
    },
    onSettled: () => refresh(),
  });

  function openInvite() {
    setInviting((n) => n + 1);
    setInviteOpen(true);
  }

  const rows = inDisplayOrder(query.data ?? []);

  const LABELS: Record<UserAction, string> = {
    changeRole: 'Change role',
    remove: 'Remove',
    resend: 'Resend invitation',
    revoke: 'Revoke invitation',
  };

  /** Each action's part of its menu item's test ID. */
  const ACTION_IDS: Record<UserAction, string> = {
    changeRole: 'change-role',
    remove: 'remove',
    resend: 'resend',
    revoke: 'revoke',
  };

  function run(action: UserAction, person: PortalUserRow) {
    if (action === 'changeRole') {
      setRoleFor(person);
      setRoleOpen(true);
    } else if (action === 'remove' || action === 'revoke') {
      setPending({ kind: action, person });
    } else if (!resend.isPending) {
      // A second Resend before the first is answered would make two fresh
      // links and two emails, and only one of them would still work.
      resend.mutate(person);
    }
  }

  /** Past rows are drawn quieter, so history does not read as live. */
  const text = (row: PortalUserRow, value: string) => (
    <Typography variant="body2" color={row.isPast ? 'text.secondary' : 'text.primary'}>
      {value}
    </Typography>
  );

  const columns: Column<PortalUserRow>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {text(row, row.name)}
          {row.isSelf ? (
            <Chip
              size="small"
              label="You"
              variant="outlined"
              data-testid="users-self-chip"
            />
          ) : null}
        </Box>
      ),
    },
    {
      key: 'email',
      header: 'Email address',
      render: (row) => (
        <Typography
          variant="body2"
          color={row.isPast ? 'text.secondary' : 'text.primary'}
          sx={{ overflowWrap: 'anywhere' }}
        >
          {row.email}
        </Typography>
      ),
    },
    { key: 'role', header: 'Role', render: (row) => text(row, ROLE_LABELS[row.role]) },
    {
      key: 'state',
      header: 'State',
      render: (row) => (
        <Box>
          {/* Text, not colour. The state has to survive being read aloud. */}
          <Chip
            size="small"
            label={STATE_LABELS[row.state]}
            color={row.state === 'active' ? 'success' : 'default'}
            variant={row.isPast ? 'outlined' : 'filled'}
            data-testid="users-state-chip"
          />
          {expiryText(row) ? (
            <Typography
              variant="caption"
              component="p"
              color="text.secondary"
              data-testid="users-expiry-caption"
              sx={{ mt: 0.5 }}
            >
              {expiryText(row)}
            </Typography>
          ) : null}
        </Box>
      ),
    },
    {
      key: 'lastLogin',
      header: 'Last sign-in',
      render: (row) => text(row, row.lastLoginAt ? formatDate(row.lastLoginAt) : 'Never'),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => {
        const { actions, note } = actionsFor(row, query.data ?? [], writable);
        if (actions.length === 0) {
          return note ? (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="users-note-message"
            >
              {note}
            </Typography>
          ) : null;
        }
        const items: RowAction[] = actions.map((action) => ({
          id: ACTION_IDS[action],
          label: LABELS[action],
          onSelect: () => run(action, row),
        }));
        return (
          <RowActionsMenu
            rowLabel={row.name}
            actions={items}
            testId="users-row-actions"
          />
        );
      },
    },
  ];

  const onlyMe = query.isSuccess && rows.length === 1 && rows[0].isSelf;

  return (
    <Box data-testid="users-page">
      <PageHeader
        title="Users"
        description="Who can use the portal for your organization, and what they may do."
        action={
          writable ? (
            <Button onClick={openInvite} data-testid="users-invite-button">
              Invite user
            </Button>
          ) : null
        }
      />

      {statusForbids ? (
        <Alert
          severity="warning"
          role="alert"
          data-testid="users-read-only-alert"
          sx={{ mb: 3, maxWidth: 760 }}
        >
          This organization is{' '}
          {(STATUS_LABELS[organization?.status ?? ''] ?? 'not active').toLowerCase()}, so
          its users cannot be changed. Contact Activist Technologies to discuss it.
        </Alert>
      ) : null}

      <DataTable
        caption="Portal users"
        testId="users-table"
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.membershipId}
        loading={query.isPending}
        error={query.isError ? 'The users could not be loaded.' : null}
        onRetry={() => void query.refetch()}
        emptyMessage="Nobody has been invited yet."
      />

      {onlyMe ? (
        <Paper
          variant="outlined"
          data-testid="users-only-me-card"
          sx={{ p: 3, mt: 2, maxWidth: 760 }}
        >
          <Typography>Nobody else has been invited yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Invite an Admin to help with content, or a second Super Admin so the
            organization is never left without one.
          </Typography>
          {writable ? (
            <Button
              variant="secondary"
              onClick={openInvite}
              data-testid="users-invite-somebody-button"
              sx={{ mt: 2 }}
            >
              Invite somebody
            </Button>
          ) : null}
        </Paper>
      ) : null}

      <InviteUserDialog
        key={inviting}
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />

      <ChangeRoleDialog
        person={roleFor}
        open={roleOpen}
        onClose={() => setRoleOpen(false)}
      />

      <ConfirmDialog
        open={pending?.kind === 'remove'}
        title={pending ? `Remove ${pending.person.name}?` : 'Remove?'}
        description={
          pending
            ? `${pending.person.name} (${pending.person.email}) will lose access to this organization immediately. Their row stays in the list, marked Removed.`
            : ''
        }
        confirmLabel="Remove"
        destructive
        pending={remove.isPending}
        testId="users-remove-dialog"
        onConfirm={() => pending && remove.mutate(pending.person)}
        onCancel={() => setPending(null)}
      />

      <ConfirmDialog
        open={pending?.kind === 'revoke'}
        title={
          pending
            ? `Revoke the invitation to ${pending.person.name}?`
            : 'Revoke the invitation?'
        }
        description={
          pending
            ? `${pending.person.name} (${pending.person.email}) will not be able to use the link they were sent. Their row stays in the list, marked Revoked.`
            : ''
        }
        confirmLabel="Revoke"
        destructive
        pending={revoke.isPending}
        testId="users-revoke-dialog"
        onConfirm={() => pending && revoke.mutate(pending.person)}
        onCancel={() => setPending(null)}
      />
    </Box>
  );
}
